import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CPageModule from '../../components/CPageModule';
import CPageModuleBrowsableTable from '../../components/CPageModuleBrowsableTable';
import { type TPandasDataFrameDict } from '../../components/TPandasDataFrameDict';
import Page from '../../components/layout/Page';
import { ETableIds } from '../../enums/ETableIds';
import { updateStateResponseStatefulAttribute, useStateExtended } from '../../helpers/helper';
import { useAppSelector } from '../../hooks/hooks';
import { type IAugmentedBackendResponse } from '../../models/IAugmentedBackendResponse';
import { type IResponse } from '../../models/IResponse';
import { type ISortingMeta } from '../../models/ISortingMeta';
import { type NotificationDTO, SvNotificationsService } from '../../services/SvNotificationServices';
import { CNotificationEntry } from './CNotificationEntry';

interface State {
  notifications: IResponse<IAugmentedBackendResponse<TPandasDataFrameDict>>;
}

const defaultSorting: ISortingMeta = {
  sortCol: 0,
  ascending: 'descending'
};

export default function PNotificationsIndex() {
  const { locale } = useAppSelector((state) => state.dashboard);
  const [searchParams, setSearchParams] = useSearchParams();

  const [state, setState, getState] = useStateExtended<State>({
    notifications: {
      state: {
        isLoading: true,
        isError: false
      }
    }
  });

  const page = Number.parseInt(searchParams.get('page') ?? '1', 10);

  const sortCol = Number.parseInt(searchParams.get('sortCol') ?? defaultSorting.sortCol.toString(), 10);

  const ascending = (searchParams.get('ascending') as ISortingMeta['ascending'] | null) ?? defaultSorting.ascending;

  const sorting: ISortingMeta = {
    sortCol,
    ascending
  };

  const fetchNotifications = async (requestedPage: number, requestedSorting: ISortingMeta) => {
    await updateStateResponseStatefulAttribute<State, typeof state.notifications.data>(
      {
        notifications: (await getState()).notifications
      },
      setState,
      async () =>
        await SvNotificationsService.getNotifications({
          page: requestedPage,
          sorting: requestedSorting
        })
    );
  };

  const handleMarkComplete = async (notificationId: string) => {
    const response = await SvNotificationsService.markNotificationComplete(notificationId);

    if (!(await response.isSuccess())) {
      return;
    }

    await fetchNotifications(page, sorting);

    const updatedState = await getState();
    const total = updatedState.notifications.data?.meta?.pagination?.total ?? 0;

    if (total === 0) {
      // Hack to clear the red bubble in the header; in the future, this should be stored in the Store.
      window.location.reload();
    }
  };

  useEffect(() => {
    void fetchNotifications(page, {
      sortCol,
      ascending
    });
  }, [locale, page, sortCol, ascending]);

  const notificationsTitle = CLocalizedTextStringStatic('notifications.title');

  return (
    <Page title={notificationsTitle}>
      <CPageModule fullWidth>
        <CPageModule submodule>
          <h2>{notificationsTitle}</h2>
        </CPageModule>

        <CPageModuleBrowsableTable
          id={ETableIds.NOTIFICATIONS}
          data={state.notifications.data?.results}
          state={state.notifications.state}
          tableMeta={state.notifications.data?.meta}
          searchBar={false}
          onUpdateTableMeta={(nextMeta) => {
            const nextPage = nextMeta.page ?? 1;
            const nextSorting = nextMeta.sorting ?? defaultSorting;

            const nextSearchParams = new URLSearchParams(searchParams);

            nextSearchParams.set('page', nextPage.toString());
            nextSearchParams.set('sortCol', nextSorting.sortCol.toString());
            nextSearchParams.set('ascending', nextSorting.ascending);

            /*
             * Updating the URL triggers the effect above.
             * Do not call fetchNotifications here, otherwise
             * the same interaction produces two API requests.
             */
            setSearchParams(nextSearchParams);
          }}
          columnConfig={[
            {
              span: 12,
              isSortable: false,
              body: {
                style: () => ({
                  padding: 0
                }),
                render: (context) => {
                  const results = state.notifications.data?.results;
                  const rowName = context.rowName;

                  if (!results) {
                    return null;
                  }

                  /*
                   * The backend supplies desc as a nested object,
                   * although TPandasDataFrameValue only declares
                   * primitive cell values.
                   */
                  const rawDescription = results.desc?.[rowName] as unknown;

                  if (rawDescription === null || typeof rawDescription !== 'object') {
                    return null;
                  }

                  const notification: NotificationDTO = {
                    id: String(results.id?.[rowName] ?? ''),
                    createdTs: String(results.createdTs?.[rowName] ?? ''),
                    desc: rawDescription as NotificationDTO['desc'],
                    url: results.url?.[rowName] == null ? null : String(results.url[rowName]),
                    modalText: results.modalText?.[rowName] == null ? null : String(results.modalText[rowName])
                  };

                  return <CNotificationEntry notification={notification} onMarkComplete={handleMarkComplete} />;
                }
              }
            },

            /*
             * The backend response contains five dataframe fields.
             * Only the first cell is visible. Its renderer uses
             * rowName to read the four hidden supporting fields.
             */
            {
              span: 0,
              isSortable: false
            },
            {
              span: 0,
              isSortable: false
            },
            {
              span: 0,
              isSortable: false
            },
            {
              span: 0,
              isSortable: false
            }
          ]}
          rowConfig={{
            header: {
              style: () => ({
                display: 'none'
              })
            }
          }}
        />
      </CPageModule>
    </Page>
  );
}
